'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Text } from '../../../../components/styled/text';
import {
  Tick01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  CircleIcon,
  Loading03Icon,
  CancelCircleIcon,
} from '@terra/ui-icons';
import { useMemo, useState } from 'react';
import { Collapsible } from '../../../../components/collapsible';
import { useTranslations } from 'next-intl';
import { type TraceToolCall, type PlanTodoItem } from '../types';
import { TodoItemSchema } from '@terra/shared-contracts-smoke-test-history';
import { z } from 'zod';

type PlanUpdateCardProps = {
  toolCall: TraceToolCall;
};

function getStatusIcon(status: PlanTodoItem['status']) {
  switch (status) {
    case 'completed':
      return <Tick01Icon size={10} />;
    case 'in_progress':
      return <Loading03Icon size={10} />;
    case 'cancelled':
      return <CancelCircleIcon size={10} />;
    case 'pending':
    default:
      return <CircleIcon size={10} />;
  }
}

function getStatusColor(status: PlanTodoItem['status']) {
  switch (status) {
    case 'completed':
      return 'fg.muted';
    case 'in_progress':
      return 'fg.default';
    case 'cancelled':
      return 'fg.error';
    case 'pending':
    default:
      return 'fg.muted';
  }
}

const LooseTodoSchema = z.object({
  id: z.string(),
  content: z.string(),
  status: z.string().optional(),
  state: z.string().optional(),
});

function todosFromToolCall(todos: unknown) {
  const items = z.array(z.unknown()).safeParse(todos);

  if (!items.success) {
    return [];
  }

  const normalized = items.data.flatMap((item) => {
    const loose = LooseTodoSchema.safeParse(item);

    if (!loose.success) {
      return [];
    }

    return [
      {
        id: loose.data.id,
        content: loose.data.content,
        status: loose.data.status ?? loose.data.state,
      },
    ];
  });
  const parseResult = z.array(TodoItemSchema).safeParse(normalized);

  return parseResult.success ? parseResult.data : [];
}

export function PlanUpdateCard({ toolCall }: PlanUpdateCardProps) {
  const t = useTranslations('designSystem.executions');
  const [isExpanded, setIsExpanded] = useState(false);
  const todos = useMemo(
    () => todosFromToolCall(toolCall.args?.todos),
    [toolCall.args?.todos],
  );
  const itemCount = todos.length;

  if (itemCount === 0) {
    return null;
  }

  return (
    <Collapsible.Root
      open={isExpanded}
      onOpenChange={({ open }) => setIsExpanded(open)}
    >
      <HStack
        w="full"
        gap={1.5}
        alignItems="center"
        py={1}
        cursor="pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        borderRadius="sm"
      >
        <Text fontSize="xs" color="fg.muted">
          {t('planUpdated')}
        </Text>

        <Text fontSize="xs" color="fg.subtle">
          •
        </Text>

        <Text fontSize="xs" color="fg.subtle">
          {t('planItemCount', { count: itemCount })}
        </Text>

        <Box display="flex" alignItems="center" color="fg.muted">
          {isExpanded ? (
            <ArrowDown01Icon size={12} />
          ) : (
            <ArrowRight01Icon size={12} />
          )}
        </Box>

        <Box flex={1} />
      </HStack>

      <Collapsible.Content>
        <VStack gap={0.5} py={2} alignItems="stretch">
          {todos.map((todo) => (
            <HStack key={todo.id} gap={1.5} alignItems="center">
              <Box
                display="flex"
                alignItems="center"
                color={getStatusColor(todo.status)}
                flexShrink={0}
              >
                {getStatusIcon(todo.status)}
              </Box>
              <Text
                fontSize="xs"
                color={todo.status === 'completed' ? 'fg.muted' : 'fg.default'}
                textDecoration={
                  todo.status === 'completed' ? 'line-through' : 'none'
                }
              >
                {todo.content}
              </Text>
            </HStack>
          ))}
        </VStack>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
